import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Breadcrumb,
  BreadcrumbItem,
  Card,
  Row,
  Col,
  Tabs,
  Tab,
  Table,
  Dropdown,
  Modal,
  Button,
  ProgressBar,
  Spinner,
} from 'react-bootstrap';
import { AuthService, CustomerService, TransRoutesService, getActionLogsForDay, getRouteApiErrors, ReportService, trackActionLog } from '../../services';
import moment from 'moment';
import {
  CUSTOMER_TYPE,
  isCustomerDischarged,
  isCustomerListActive,
  PAY_SOURCE,
  PAY_SOURCE_TEXT,
} from '../../shared';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import { Download } from 'react-bootstrap-icons';
import { downloadXlsxFromMatrix } from '../../shared';
import './AdminView.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
);

const normalizedCustomerType = (customer) =>
  `${customer?.type || ''}`.toLowerCase().replace(/\s+/g, '_');

/** Count discharge reasons for members only (exclude visitors). */
const isDischargeStatsMember = (customer) => normalizedCustomerType(customer) !== CUSTOMER_TYPE.VISITOR;

/** Admin statistics: list-active customers only; visitors excluded from all member metrics. */
const isCustomerActiveForStats = (customer) => {
  if (!customer || !isCustomerListActive(customer)) return false;
  const t = normalizedCustomerType(customer);
  if (t === CUSTOMER_TYPE.VISITOR) return false;
  return true;
};

const isCustomerTypeMember = (customer) =>
  normalizedCustomerType(customer) === CUSTOMER_TYPE.MEMBER;

/** Active list members (type === member) who existed on `date`.
 *  Matches Customer Information → Active Customers filtered to Member.
 *  Historical days exclude members created after that day. Stale discharge dates
 *  on still-active records are ignored so today matches the customer list. */
const countMembersOnDate = (allCustomers, date) => {
  const day = moment(date).startOf('day');
  const isToday = day.isSame(moment(), 'day');
  return (allCustomers || []).filter((customer) => {
    if (!customer) return false;
    if (!isCustomerTypeMember(customer)) return false;
    if (!isCustomerListActive(customer)) return false;
    // Today matches the Active + Member list (no create_date cutoff).
    if (!isToday && customer.create_date && moment(customer.create_date).isAfter(day, 'day')) {
      return false;
    }
    return true;
  }).length;
};

/** Resolve calendar month from dropdown value (`this-month` | `month-N`). */
const getMonthRangeFromSelection = (monthSelection) => {
  if (monthSelection === 'this-month') {
    const targetMonth = moment();
    return {
      targetMonth,
      startDate: targetMonth.clone().startOf('month'),
      endDate: targetMonth.clone().endOf('month'),
    };
  }
  const monthNumber = parseInt(monthSelection.split('-')[1], 10);
  const targetMonth = moment().subtract(monthNumber, 'months');
  return {
    targetMonth,
    startDate: targetMonth.clone().startOf('month'),
    endDate: targetMonth.clone().endOf('month'),
  };
};

/** Do not plot member/attendance trends for days after today (e.g. rest of current month). */
const capTrendChartEndDate = (startDate, endDate) => {
  const today = moment().startOf('day');
  const monthEnd = endDate.clone().startOf('day');
  return today.isBefore(monthEnd) ? today.clone() : monthEnd.clone();
};

/** Matches `customer.pay_source` to PAY_SOURCE enum; unknown/empty → __UNSPECIFIED__. */
const PAY_SOURCE_STATS_UNSPECIFIED = '__UNSPECIFIED__';

const resolveCustomerPaySourceKey = (customer) => {
  const raw = customer?.pay_source;
  if (raw === undefined || raw === null || `${raw}`.trim() === '') {
    return PAY_SOURCE_STATS_UNSPECIFIED;
  }
  const normalized = `${raw}`.trim().toLowerCase();
  const match = Object.values(PAY_SOURCE).find((v) => v.toLowerCase() === normalized);
  return match || PAY_SOURCE_STATS_UNSPECIFIED;
};

const PAY_SOURCE_STATS_ORDER = [...Object.values(PAY_SOURCE), PAY_SOURCE_STATS_UNSPECIFIED];

const PAY_SOURCE_STATS_LABELS = PAY_SOURCE_STATS_ORDER.reduce((acc, key) => {
  if (key === PAY_SOURCE_STATS_UNSPECIFIED) {
    acc[key] = 'Not specified';
  } else {
    acc[key] = PAY_SOURCE_TEXT[key] || key;
  }
  return acc;
}, {});

const PAY_SOURCE_CHART_COLORS = [
  'rgb(255, 205, 86)',
  'rgb(255, 99, 132)',
  'rgb(54, 162, 235)',
  'rgb(75, 192, 192)',
  'rgb(153, 102, 255)',
  'rgb(201, 203, 207)',
];

/** Admin pie: fixed 5 pay types (Other includes “Not specified” / empty pay source). */
const PAY_SOURCE_PIE_LABELS = ['Private Pay', 'Medicaid', 'QMB', 'Veteran', 'Other'];
const PAY_SOURCE_PIE_COLORS = [
  'rgb(255, 205, 86)',
  'rgb(255, 99, 132)',
  'rgb(54, 162, 235)',
  'rgb(75, 192, 192)',
  'rgb(153, 102, 255)',
];

/** Shared hover tooltip for Admin View doughnut charts (count + % of total). */
const doughnutHoverTooltip = {
  callbacks: {
    label: (ctx) => {
      const d = ctx.dataset?.data?.[ctx.dataIndex];
      const total = (ctx.dataset?.data || []).reduce((a, b) => a + b, 0) || 0;
      const pct = total > 0 ? Math.round((d / total) * 100) : 0;
      return `${ctx.label || ''}: ${d} (${pct}%)`;
    },
  },
};

/** Admit From: 5 groups (AMDC = all `amdc*` values). */
const ADMIT_FROM_PIE_LABELS = [
  'New to the Program',
  'Transfer from Another Worldshine Center',
  'Transfer from Another AMDC Center',
  'N/A',
  'Other',
];
const ADMIT_FROM_WS = new Set(['ws1', 'ws2', 'ws3']);
const mapAdmittedFromToPieGroup = (v) => {
  if (v == null || v === '') return 'N/A';
  if (v === 'newToProgram') return 'New to the Program';
  if (v === 'na') return 'N/A';
  if (v === 'other') return 'Other';
  if (ADMIT_FROM_WS.has(v)) return 'Transfer from Another Worldshine Center';
  if (String(v).startsWith('amdc')) return 'Transfer from Another AMDC Center';
  return 'Other';
};

/** New Admissions (referral / how heard): 9 groups; social sub-channels → Social Media. */
const NEW_ADMISSIONS_PIE_LABELS = [
  'Family and Friends',
  'Healthcare Provider',
  'Social Worker',
  'Support Planner',
  'Online Search',
  'Social Media',
  'Event',
  'N/A',
  'Other',
];
const NEW_ADMISSIONS_SOCIAL_MEDIA_KEYS = new Set([
  'facebook',
  'instagram',
  'rednote',
  'youtube',
  'wechat',
  'newspaper',
  'nextdoor',
  'socialMedia', // legacy join_reason
]);
const mapNewMemberToReferralPieGroup = (customer) => {
  const r = customer?.referral_source;
  if (r) {
    if (NEW_ADMISSIONS_SOCIAL_MEDIA_KEYS.has(r)) return 'Social Media';
    if (r === 'familyFriends') return 'Family and Friends';
    if (r === 'healthcareProvider') return 'Healthcare Provider';
    if (r === 'socialWorker') return 'Social Worker';
    if (r === 'supportPlanner') return 'Support Planner';
    if (r === 'onlineSearch') return 'Online Search';
    if (r === 'event') return 'Event';
    if (r === 'na') return 'N/A';
    if (r === 'other') return 'Other';
    if (r === 'rejoin' || r === 'transferWorldshine' || r === 'transferAmdc') return 'N/A';
    return 'Other';
  }
  const j = customer?.join_reason;
  if (j === 'friendFamilyReferral') return 'Family and Friends';
  if (j === 'socialWorkerReferral') return 'Social Worker';
  if (j === 'socialMedia') return 'Social Media';
  if (j === 'event') return 'Event';
  if (j === 'other') return 'Other';
  return 'N/A';
};

/** Discharge: 10 groups; WS/AMDC center codes roll up to one slice each. */
const DISCHARGE_PIE_LABELS = [
  'Absent for Over 30 Days',
  'Transfer to Another Worldshine Center',
  'Transfer to Another AMDC Center',
  'Transfer to Another Type of Facility',
  'Deceased',
  'Hospitalized',
  'Moving out of the Service Area',
  'No Longer Eligible for ADS',
  'N/A',
  'Other',
];
const mapDischargeReasonToPieGroup = (customer) => {
  const d = customer?.discharge_reason;
  if (d == null || d === '') return 'N/A';
  if (d === 'absentOver30') return 'Absent for Over 30 Days';
  if (d === 'ws1' || d === 'ws2' || d === 'ws3' || d === 'transferWorldshine') {
    return 'Transfer to Another Worldshine Center';
  }
  if (d === 'transferAmdc' || (typeof d === 'string' && d.startsWith('amdc'))) {
    return 'Transfer to Another AMDC Center';
  }
  if (d === 'transferOtherFacility') return 'Transfer to Another Type of Facility';
  if (d === 'deceased') return 'Deceased';
  if (d === 'hospitalized') return 'Hospitalized';
  if (d === 'movingOutServiceArea') return 'Moving out of the Service Area';
  if (d === 'noLongerEligible') return 'No Longer Eligible for ADS';
  if (d === 'na') return 'N/A';
  if (d === 'other') return 'Other';
  return 'Other';
};

const NEW_ADMISSIONS_PIE_COLORS = [
  'rgb(255, 99, 132)',
  'rgb(54, 162, 235)',
  'rgb(255, 205, 86)',
  'rgb(75, 192, 192)',
  'rgb(153, 102, 255)',
  'rgb(201, 203, 207)',
  'rgb(255, 159, 64)',
  'rgb(100, 149, 237)',
  'rgb(46, 204, 113)',
];
const ADMIT_FROM_PIE_COLORS = [
  'rgb(100, 149, 237)',
  'rgb(255, 99, 132)',
  'rgb(54, 162, 235)',
  'rgb(201, 203, 207)',
  'rgb(255, 205, 86)',
];
const DISCHARGE_PIE_COLORS = [
  'rgb(255, 99, 132)',
  'rgb(54, 162, 235)',
  'rgb(255, 205, 86)',
  'rgb(75, 192, 192)',
  'rgb(153, 102, 255)',
  'rgb(201, 203, 207)',
  'rgb(255, 159, 64)',
  'rgb(46, 204, 113)',
  'rgb(100, 149, 237)',
  'rgb(199, 199, 199)',
];

const AdminView = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [selectedTrendMonth, setSelectedTrendMonth] = useState('this-month');
  const [selectedMonth, setSelectedMonth] = useState('this-month');
  const [selectedDischargeMonth, setSelectedDischargeMonth] = useState('this-month');
  const [memberTrendData, setMemberTrendData] = useState({
    labels: [],
    datasets: []
  });
  const [attendanceTrendData, setAttendanceTrendData] = useState({
    labels: [],
    datasets: []
  });
  const [attendanceTrendLoading, setAttendanceTrendLoading] = useState(false);
  const attendanceTrendRequestIdRef = useRef(0);
  const [paySourceData, setPaySourceData] = useState({
    labels: [],
    datasets: []
  });
  const [newAdmissionsData, setNewAdmissionsData] = useState({
    labels: [],
    datasets: []
  });
  const [dischargeReasonsData, setDischargeReasonsData] = useState({
    labels: [],
    datasets: []
  });
  const [admitFromData, setAdmitFromData] = useState({
    labels: [],
    datasets: []
  });
  
  // Mock data for demonstration - replace with actual API calls
  const [statsData, setStatsData] = useState({
    totalMembers: 0,
    totalMembersIncreaseRate: 0,
    totalMembersTrend: '',
    todayAttendance: 0,
    todayAttendanceChangeRate: 0,
    todayAttendanceTrend: '',
    newMembersThisMonth: 0,
    newMembersChangeRate: 0,
    newMembersTrend: '',
    memberDischargesThisMonth: 0,
    dischargesChangeRate: 0,
    dischargesTrend: ''
  });

  const [activeAdminTab, setActiveAdminTab] = useState('statistics');
  const [actionLogDate, setActionLogDate] = useState(() => moment().format('YYYY-MM-DD'));
  const [actionLogEntries, setActionLogEntries] = useState([]);
  const [actionLogLoading, setActionLogLoading] = useState(false);
  const canViewApiLog = AuthService.canViewApiLog();
  const [apiLogDate, setApiLogDate] = useState(() => moment().format('YYYY-MM-DD'));
  const [apiLogEntries, setApiLogEntries] = useState([]);
  const [apiLogLoading, setApiLogLoading] = useState(false);
  const [expandedApiLogId, setExpandedApiLogId] = useState(null);
  const filteredActionLogEntries = useMemo(() => {
    const shouldHideUserLog = (row) => {
      const userLabel = `${row?.userDisplayName || row?.userId || ''}`.toLowerCase();
      return userLabel.includes('admin') || userLabel.includes('test');
    };
    return (actionLogEntries || []).filter((row) => !shouldHideUserLog(row));
  }, [actionLogEntries]);

  const [adminExportLoading, setAdminExportLoading] = useState(false);
  const [showDriverTimesheetModal, setShowDriverTimesheetModal] = useState(false);
  const [driverTimesheetDate, setDriverTimesheetDate] = useState('');
  const [showDriverWeeklyTimesheetModal, setShowDriverWeeklyTimesheetModal] = useState(false);
  const [driverWeeklyTimesheetMonday, setDriverWeeklyTimesheetMonday] = useState('');

  useEffect(() => {
    if (activeAdminTab !== 'actionLog') return undefined;
    let cancelled = false;
    setActionLogLoading(true);
    getActionLogsForDay(actionLogDate)
      .then((res) => {
        if (!cancelled) setActionLogEntries(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setActionLogEntries([]);
      })
      .finally(() => {
        if (!cancelled) setActionLogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeAdminTab, actionLogDate]);

  useEffect(() => {
    if (!canViewApiLog || activeAdminTab !== 'apiLog') return undefined;
    let cancelled = false;
    setApiLogLoading(true);
    getRouteApiErrors(apiLogDate)
      .then((res) => {
        if (!cancelled) setApiLogEntries(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setApiLogEntries([]);
      })
      .finally(() => {
        if (!cancelled) setApiLogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeAdminTab, apiLogDate, canViewApiLog]);

  useEffect(() => {
    // Check if user has admin access
    if (!AuthService.canViewAdminView()) {
      window.alert('You haven\'t login yet OR this user does not have access to this page. Please change an admin account to login.');
      AuthService.logout();
      window.location.href = '/login';
      return;
    }
    
    // Load initial data
    loadCustomerData();
    setIsLoading(false);
  }, []);

  const loadCustomerData = async () => {
    try {
      // Fetch all customers
      const customersData = await CustomerService.getAllCustomers();
      const allCustomers = customersData.data;
      setCustomers(allCustomers);

      // Total Members card = same count as today's point on the Total Members graph.
      const totalMembers = countMembersOnDate(allCustomers, moment());

      // Generate member / attendance trend for the selected month
      generateMemberTrendData(allCustomers, selectedTrendMonth);
      generateAttendanceTrendData(selectedTrendMonth);
      generatePaySourceData(allCustomers);
      generateNewAdmissionsData(allCustomers, selectedMonth);
      generateAdmitFromData(allCustomers, selectedMonth);
      generateDischargeReasonsData(allCustomers, selectedDischargeMonth);

      // New members this month = members on this month's last day − members on last month's last day.
      // For the current (incomplete) month, "last day" is today (same as trend charts).
      // Last month uses the same definition so the increase % is this-month new vs last-month new.
      const thisMonthLastDay = capTrendChartEndDate(
        moment().startOf('month'),
        moment().endOf('month')
      );
      const lastMonthLastDay = moment().subtract(1, 'month').endOf('month');
      const monthBeforeLastLastDay = moment().subtract(2, 'month').endOf('month');

      const membersThisMonthLastDay = countMembersOnDate(allCustomers, thisMonthLastDay);
      const membersLastMonthLastDay = countMembersOnDate(allCustomers, lastMonthLastDay);
      const membersMonthBeforeLastLastDay = countMembersOnDate(allCustomers, monthBeforeLastLastDay);

      const newMembersThisMonth = membersThisMonthLastDay - membersLastMonthLastDay;
      const newMembersLastMonth = membersLastMonthLastDay - membersMonthBeforeLastLastDay;

      let newMembersChangeRate = 0;
      let newMembersTrend = '';
      if (newMembersLastMonth !== 0) {
        const percentageChange =
          ((newMembersThisMonth - newMembersLastMonth) / Math.abs(newMembersLastMonth)) * 100;
        newMembersChangeRate = Math.abs(percentageChange);
        newMembersTrend = percentageChange >= 0 ? 'increase' : 'decrease';
      }

      // Calculate member discharges this month
      const currentMonthDischarge = moment();
      const memberDischargesThisMonth = allCustomers.filter(customer => {
        if (!customer.discharge_date) return false;
        if (normalizedCustomerType(customer) === CUSTOMER_TYPE.VISITOR) return false;
        const dischargeDate = moment(customer.discharge_date, 'MM/DD/YYYY');
        return dischargeDate.isSame(currentMonthDischarge, 'month');
      }).length;

      // Calculate member discharges last month
      const lastMonthDischarge = moment().subtract(1, 'month');
      const memberDischargesLastMonth = allCustomers.filter(customer => {
        if (!customer.discharge_date) return false;
        if (normalizedCustomerType(customer) === CUSTOMER_TYPE.VISITOR) return false;
        const dischargeDate = moment(customer.discharge_date, 'MM/DD/YYYY');
        return dischargeDate.isSame(lastMonthDischarge, 'month');
      }).length;

      // Calculate discharges change rate
      let dischargesChangeRate = 0;
      let dischargesTrend = '';
      if (memberDischargesLastMonth > 0) {
        const percentageChange = ((memberDischargesThisMonth - memberDischargesLastMonth) / memberDischargesLastMonth) * 100;
        dischargesChangeRate = Math.abs(percentageChange);
        dischargesTrend = percentageChange >= 0 ? 'increase' : 'decrease';
      } else if (memberDischargesThisMonth > 0) {
        dischargesChangeRate = 100;
        dischargesTrend = 'increase';
      }

      // Month-over-month total members: current active count vs end of previous calendar month.
      const endOfLastMonth = moment().subtract(1, 'month').endOf('month');
      const totalMembersLastMonth = countMembersOnDate(allCustomers, endOfLastMonth);
      let totalMembersIncreaseRate = 0;
      let totalMembersTrend = '';
      if (totalMembersLastMonth > 0) {
        const percentageChange =
          ((totalMembers - totalMembersLastMonth) / totalMembersLastMonth) * 100;
        totalMembersIncreaseRate = Math.abs(percentageChange);
        totalMembersTrend = percentageChange >= 0 ? 'increase' : 'decrease';
      } else if (totalMembers > 0) {
        totalMembersIncreaseRate = 100;
        totalMembersTrend = 'increase';
      }

      // Today's / yesterday's attendance — same rules as Dashboard (inbound: customers with In Center status).
      const today = moment().format('MM/DD/YYYY');
      const todayRoutesData = await TransRoutesService.getAll(today);
      const todayInboundRoutes = (Array.isArray(todayRoutesData?.data) ? todayRoutesData.data : []).filter(
        (route) => route.type === 'inbound'
      );
      const todayAttendance = TransRoutesService.getRouteSectionManifestStats(todayInboundRoutes).checkedInCount;

      const yesterday = moment().subtract(1, 'day').format('MM/DD/YYYY');
      const yesterdayRoutesData = await TransRoutesService.getAll(yesterday);
      const yesterdayInboundRoutes = (Array.isArray(yesterdayRoutesData?.data) ? yesterdayRoutesData.data : []).filter(
        (route) => route.type === 'inbound'
      );
      const yesterdayAttendance = TransRoutesService.getRouteSectionManifestStats(yesterdayInboundRoutes).checkedInCount;

      // Calculate attendance change rate
      let attendanceChangeRate = 0;
      let attendanceTrend = '';
      if (yesterdayAttendance > 0) {
        const percentageChange = ((todayAttendance - yesterdayAttendance) / yesterdayAttendance) * 100;
        attendanceChangeRate = Math.abs(percentageChange);
        attendanceTrend = percentageChange >= 0 ? 'increase' : 'decrease';
      } else if (todayAttendance > 0) {
        attendanceChangeRate = 100;
        attendanceTrend = 'increase';
      }

      setStatsData(prevData => ({
        ...prevData,
        totalMembers,
        totalMembersIncreaseRate,
        totalMembersTrend,
        newMembersThisMonth,
        newMembersChangeRate,
        newMembersTrend,
        todayAttendance,
        todayAttendanceChangeRate: attendanceChangeRate,
        todayAttendanceTrend: attendanceTrend,
        memberDischargesThisMonth,
        dischargesChangeRate,
        dischargesTrend
      }));

    } catch (error) {
      console.error('Error fetching customer data:', error);
    }
  };

  // Generate month options for the past 12 months
  const generateMonthOptions = () => {
    const options = [];
    
    // This month
    options.push({
      value: 'this-month',
      label: 'This Month'
    });
    
    // Past 12 months
    for (let i = 1; i <= 12; i++) {
      const month = moment().subtract(i, 'months');
      options.push({
        value: `month-${i}`,
        label: month.format('MMMM YYYY')
      });
    }
    
    return options;
  };

  // Generate member trend data for the selected calendar month (one point per day)
  const generateMemberTrendData = (allCustomers, monthSelection) => {
    const { startDate, endDate } = getMonthRangeFromSelection(monthSelection);
    const startDay = startDate.clone().startOf('day');
    const trendEnd = capTrendChartEndDate(startDate, endDate);

    const labels = [];
    const data = [];
    const dayCount = trendEnd.diff(startDay, 'days') + 1;
    const pointRadius = dayCount > 14 ? 3 : 5;
    const pointHoverRadius = dayCount > 14 ? 5 : 8;

    for (let i = 0; i < dayCount; i++) {
      const currentDate = startDay.clone().add(i, 'days');
      labels.push(currentDate.format('MMM D'));

      data.push(countMembersOnDate(allCustomers, currentDate));
    }

    const average =
      data.length > 0 ? Math.round(data.reduce((sum, val) => sum + val, 0) / data.length) : 0;

    setMemberTrendData({
      labels,
      datasets: [
        {
          label: 'Active Members',
          data,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          tension: 0.1,
          pointRadius,
          pointHoverRadius,
        },
      ],
      average,
    });
  };

  // Generate attendance trend for the selected calendar month (one point per day).
  // Uses the same In Center count as the Today's Attendance card.
  const generateAttendanceTrendData = async (monthSelection) => {
    const requestId = attendanceTrendRequestIdRef.current + 1;
    attendanceTrendRequestIdRef.current = requestId;
    setAttendanceTrendLoading(true);

    const { startDate, endDate } = getMonthRangeFromSelection(monthSelection);
    const startDay = startDate.clone().startOf('day');
    const trendEnd = capTrendChartEndDate(startDate, endDate);

    const labels = [];
    const dates = [];
    const dayCount = trendEnd.diff(startDay, 'days') + 1;
    const pointRadius = dayCount > 14 ? 3 : 5;
    const pointHoverRadius = dayCount > 14 ? 5 : 8;

    for (let i = 0; i < dayCount; i++) {
      const currentDate = startDay.clone().add(i, 'days');
      dates.push(currentDate.format('MM/DD/YYYY'));
      labels.push(currentDate.format('MMM D'));
    }

    let countsByDate = {};
    try {
      try {
        const response = await TransRoutesService.getAttendanceCounts(dates[0], dates[dates.length - 1]);
        countsByDate = response?.data?.counts || {};
      } catch (error) {
        console.error('Error fetching attendance counts:', error);
        const batchSize = 6;
        for (let i = 0; i < dates.length; i += batchSize) {
          if (requestId !== attendanceTrendRequestIdRef.current) return;
          const chunk = dates.slice(i, i + batchSize);
          const results = await Promise.all(
            chunk.map(async (dateString) => {
              try {
                const routesData = await TransRoutesService.getAll(dateString);
                const inboundRoutes = (Array.isArray(routesData?.data) ? routesData.data : []).filter(
                  (route) => route.type === 'inbound'
                );
                return [
                  dateString,
                  TransRoutesService.getRouteSectionManifestStats(inboundRoutes).checkedInCount,
                ];
              } catch (dayError) {
                console.error(`Error fetching routes for ${dateString}:`, dayError);
                return [dateString, 0];
              }
            })
          );
          results.forEach(([dateString, count]) => {
            countsByDate[dateString] = count;
          });
        }
      }

      if (requestId !== attendanceTrendRequestIdRef.current) return;

      const data = dates.map((dateString) => Number(countsByDate[dateString]) || 0);
      const nonZeroAttendanceDays = data.filter((val) => val > 0);
      const average =
        nonZeroAttendanceDays.length > 0
          ? Math.round(
              nonZeroAttendanceDays.reduce((sum, val) => sum + val, 0) / nonZeroAttendanceDays.length
            )
          : 0;

      setAttendanceTrendData({
        labels,
        datasets: [
          {
            label: 'Attendance',
            data,
            borderColor: 'rgb(255, 99, 132)',
            backgroundColor: 'rgba(255, 99, 132, 0.5)',
            tension: 0.1,
            pointRadius,
            pointHoverRadius,
          },
        ],
        average,
      });
    } finally {
      if (requestId === attendanceTrendRequestIdRef.current) {
        setAttendanceTrendLoading(false);
      }
    }
  };

  // Generate pay source distribution data — fixed 5 slices: Private Pay, Medicaid, QMB, Veteran, Other (includes not specified)
  const generatePaySourceData = (allCustomers) => {
    const activeCustomers = allCustomers.filter(isCustomerActiveForStats);
    const totalActive = activeCustomers.length;

    let privatePay = 0;
    let medicaid = 0;
    let qmb = 0;
    let veteran = 0;
    let other = 0;

    activeCustomers.forEach((customer) => {
      const key = resolveCustomerPaySourceKey(customer);
      if (key === PAY_SOURCE.PRIVATE_PAY) privatePay += 1;
      else if (key === PAY_SOURCE.MEDICAID) medicaid += 1;
      else if (key === PAY_SOURCE.QMB) qmb += 1;
      else if (key === PAY_SOURCE.VETERAN) veteran += 1;
      else other += 1;
    });

    const data = [privatePay, medicaid, qmb, veteran, other];
    const labels = [...PAY_SOURCE_PIE_LABELS];
    const percentages = data.map((count) =>
      totalActive > 0 ? Math.round((count / totalActive) * 100) : 0
    );
    const colors = [...PAY_SOURCE_PIE_COLORS];

    setPaySourceData({
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors,
          borderColor: colors.map((color) => color.replace('rgb', 'rgba').replace(')', ', 0.8)')),
          borderWidth: 2,
          hoverOffset: 4,
        },
      ],
      percentages,
      totalActive,
    });
  };

  // New Admissions pie: fixed referral groups (referral_source; legacy join_reason); social sub-channels → Social Media
  const generateNewAdmissionsData = (allCustomers, monthSelection = 'this-month') => {
    let targetMonth;
    if (monthSelection === 'this-month') {
      targetMonth = moment();
    } else {
      const monthNumber = parseInt(monthSelection.split('-')[1], 10);
      targetMonth = moment().subtract(monthNumber, 'months');
    }

    const customersInMonth = allCustomers.filter(
      (customer) => moment(customer.create_date).isSame(targetMonth, 'month') && isCustomerActiveForStats(customer)
    );

    const totalInMonth = customersInMonth.length;
    const counts = NEW_ADMISSIONS_PIE_LABELS.reduce((acc, label) => {
      acc[label] = 0;
      return acc;
    }, {});

    customersInMonth.forEach((customer) => {
      const g = mapNewMemberToReferralPieGroup(customer);
      counts[g] = (counts[g] || 0) + 1;
    });

    const labels = [...NEW_ADMISSIONS_PIE_LABELS];
    const data = labels.map((l) => counts[l] || 0);
    const percentages = data.map((count) => (totalInMonth > 0 ? Math.round((count / totalInMonth) * 100) : 0));
    const colors = NEW_ADMISSIONS_PIE_COLORS;

    setNewAdmissionsData({
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors,
          borderColor: colors.map((color) => color.replace('rgb', 'rgba').replace(')', ', 0.8)')),
          borderWidth: 2,
          hoverOffset: 4,
        },
      ],
      percentages,
      totalInMonth,
    });
  };

  // Admit From: new members in month; fixed 5 groups (WS/AMDC roll-up)
  const generateAdmitFromData = (allCustomers, monthSelection = 'this-month') => {
    const { targetMonth } = getMonthRangeFromSelection(monthSelection);
    const inMonth = allCustomers.filter(
      (customer) =>
        moment(customer.create_date).isSame(targetMonth, 'month') && isCustomerActiveForStats(customer)
    );
    const total = inMonth.length;
    const counts = ADMIT_FROM_PIE_LABELS.reduce((acc, label) => {
      acc[label] = 0;
      return acc;
    }, {});

    inMonth.forEach((customer) => {
      const g = mapAdmittedFromToPieGroup(customer.admitted_from);
      counts[g] = (counts[g] || 0) + 1;
    });

    const labels = [...ADMIT_FROM_PIE_LABELS];
    const data = labels.map((l) => counts[l] || 0);
    const percentages = data.map((count) => (total > 0 ? Math.round((count / total) * 100) : 0));
    const colors = ADMIT_FROM_PIE_COLORS;

    setAdmitFromData({
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors,
          borderColor: colors.map((c) => c.replace('rgb', 'rgba').replace(')', ', 0.8)')),
          borderWidth: 2,
          hoverOffset: 4,
        },
      ],
      percentages,
      totalInMonth: total,
    });
  };

  // Generate discharge reasons distribution data for a calendar month
  const generateDischargeReasonsData = (allCustomers, monthSelection = 'this-month') => {
    console.log('generateDischargeReasonsData called with:', allCustomers?.length, 'customers, month:', monthSelection);

    const { targetMonth } = getMonthRangeFromSelection(monthSelection);
    console.log('Target month:', targetMonth.format('MMMM YYYY'));

    const dischargedCustomers = allCustomers.filter((customer) => {
      if (!isCustomerDischarged(customer)) return false;
      if (!isDischargeStatsMember(customer)) return false;
      if (!customer.discharge_date) return false;

      const dischargeDate = moment(customer.discharge_date, 'MM/DD/YYYY');
      return dischargeDate.isSame(targetMonth, 'month');
    });

    console.log('Discharged customers in selected month:', dischargedCustomers.length);
    
    const totalDischarged = dischargedCustomers.length;

    const dischargeReasonCounts = DISCHARGE_PIE_LABELS.reduce((acc, label) => {
      acc[label] = 0;
      return acc;
    }, {});

    dischargedCustomers.forEach((customer) => {
      const g = mapDischargeReasonToPieGroup(customer);
      dischargeReasonCounts[g] = (dischargeReasonCounts[g] || 0) + 1;
    });

    const labels = [...DISCHARGE_PIE_LABELS];
    const data = labels.map((l) => dischargeReasonCounts[l] || 0);
    const percentages = data.map((count) =>
      totalDischarged > 0 ? Math.round((count / totalDischarged) * 100) : 0
    );
    const colors = [...DISCHARGE_PIE_COLORS];

    const dischargeReasonsData = {
      labels,
      datasets: [{
        data,
        backgroundColor: colors,
        borderColor: colors.map(color => color.replace('rgb', 'rgba').replace(')', ', 0.8)')),
        borderWidth: 2,
        hoverOffset: 4
      }],
      percentages,
      totalDischarged
    };
    
    console.log('Setting dischargeReasonsData:', dischargeReasonsData);
    setDischargeReasonsData(dischargeReasonsData);
  };

  // Member + attendance line charts share the same month
  const handleTrendMonthChange = (event) => {
    const newMonth = event.target.value;
    setSelectedTrendMonth(newMonth);
    generateMemberTrendData(customers, newMonth);
    generateAttendanceTrendData(newMonth);
  };

  // Handle month selection change
  const handleMonthChange = (event) => {
    const newMonth = event.target.value;
    setSelectedMonth(newMonth);
    generateNewAdmissionsData(customers, newMonth);
    generateAdmitFromData(customers, newMonth);
  };

  const handleDischargeMonthChange = (event) => {
    const newMonth = event.target.value;
    setSelectedDischargeMonth(newMonth);
    generateDischargeReasonsData(customers, newMonth);
  };

  const handleExportAdminView = async () => {
    if (activeAdminTab === 'actionLog') {
      const rows = [
        ['Time', 'User', 'Action'],
        ...filteredActionLogEntries.map((row) => [
          moment(row.eventTime).format('LTS'),
          row.userDisplayName || row.userId || '—',
          row.summary ?? '',
        ]),
      ];
      await downloadXlsxFromMatrix(rows, `admin-view-action-log-${actionLogDate}`, 'Action log');
      return;
    }

    const rows = [
      ['Metric', 'Value'],
      ['Total Members', statsData.totalMembers],
      ['Total Members increase % from last month', statsData.totalMembersIncreaseRate],
      ["Today's Attendance", statsData.todayAttendance],
      ['Today vs yesterday attendance change %', statsData.todayAttendanceChangeRate],
      ['New Members This Month', statsData.newMembersThisMonth],
      ['New Members change % from last month', statsData.newMembersChangeRate],
      ['Member Discharges This Month', statsData.memberDischargesThisMonth],
      ['Discharges change % from last month', statsData.dischargesChangeRate],
    ];

    rows.push(['', '']);
    rows.push(['Total Members trend (selected month)', '']);
    rows.push(['Day', 'Active members']);
    (memberTrendData.labels || []).forEach((label, i) => {
      rows.push([label, memberTrendData.datasets?.[0]?.data?.[i] ?? '']);
    });
    rows.push(['Average', memberTrendData.average ?? '']);

    rows.push(['', '']);
    rows.push(['Total Attendance trend (selected month)', '']);
    rows.push(['Day', 'Attendance count']);
    (attendanceTrendData.labels || []).forEach((label, i) => {
      rows.push([label, attendanceTrendData.datasets?.[0]?.data?.[i] ?? '']);
    });
    rows.push(['Average', attendanceTrendData.average ?? '']);

    rows.push(['', '']);
    rows.push(['Pay source (active members)', '']);
    rows.push(['Source', 'Count', 'Percent']);
    (paySourceData.labels || []).forEach((label, i) => {
      const count = paySourceData.datasets?.[0]?.data?.[i];
      const pct = paySourceData.percentages?.[i];
      rows.push([label, count ?? '', pct != null ? `${pct}%` : '']);
    });

    rows.push(['', '']);
    rows.push(['New admissions by join reason (selected month)', '']);
    rows.push(['Reason', 'Count', 'Percent']);
    (newAdmissionsData.labels || []).forEach((label, i) => {
      const count = newAdmissionsData.datasets?.[0]?.data?.[i];
      const pct = newAdmissionsData.percentages?.[i];
      rows.push([label, count ?? '', pct != null ? `${pct}%` : '']);
    });

    rows.push(['', '']);
    rows.push(['Discharge reasons — members only, excludes visitors (selected month)', '']);
    rows.push(['Reason', 'Count', 'Percent']);
    (dischargeReasonsData.labels || []).forEach((label, i) => {
      const count = dischargeReasonsData.datasets?.[0]?.data?.[i];
      const pct = dischargeReasonsData.percentages?.[i];
      rows.push([label, count ?? '', pct != null ? `${pct}%` : '']);
    });

    rows.push(['', '']);
    rows.push(['Admit From — new members in selected month (members, excludes visitors)', '']);
    rows.push(['Admitted from', 'Count', 'Percent']);
    (admitFromData.labels || []).forEach((label, i) => {
      const count = admitFromData.datasets?.[0]?.data?.[i];
      const pct = admitFromData.percentages?.[i];
      rows.push([label, count ?? '', pct != null ? `${pct}%` : '']);
    });

    await downloadXlsxFromMatrix(
      rows,
      `admin-view-statistics-${moment().format('YYYY-MM-DD-HHmm')}`,
      'Statistics'
    );
  };

  const runChartDataExport = async () => {
    setAdminExportLoading(true);
    try {
      await new Promise((r) => setTimeout(r, 200));
      await handleExportAdminView();
    } catch (e) {
      console.error(e);
      window.alert('Could not create Excel file.');
    } finally {
      setAdminExportLoading(false);
    }
  };

  const openDriverTimesheetModal = () => {
    setDriverTimesheetDate('');
    setShowDriverTimesheetModal(true);
  };

  const openDriverWeeklyTimesheetModal = () => {
    setDriverWeeklyTimesheetMonday('');
    setShowDriverWeeklyTimesheetModal(true);
  };

  const onDriverWeeklyTimesheetDateChange = (e) => {
    const v = e.target.value;
    if (!v) {
      setDriverWeeklyTimesheetMonday('');
      return;
    }
    const picked = moment(v, 'YYYY-MM-DD').startOf('day');
    const today = moment().startOf('day');
    if (picked.isAfter(today)) {
      setDriverWeeklyTimesheetMonday('');
      return;
    }
    if (picked.isoWeekday() !== 1) {
      window.alert('Please choose a Monday (the start of the work week).');
      setDriverWeeklyTimesheetMonday('');
      return;
    }
    setDriverWeeklyTimesheetMonday(v);
  };

  const exportDriverDailyTimesheet = async () => {
    if (!driverTimesheetDate) return;
    trackActionLog({
      actionVerb: "Export",
      summary: "Tap export button (driver daily timesheet)",
      entityType: "export",
      entityId: "driver-daily-timesheet",
    }).catch(() => {});
    const mmddyyyy = moment(driverTimesheetDate, 'YYYY-MM-DD').format('MM/DD/YYYY');
    setShowDriverTimesheetModal(false);
    setAdminExportLoading(true);
    try {
      const response = await ReportService.exportDriverDailyTimesheetExcel(mmddyyyy);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `Driver_Daily_Timesheet_${driverTimesheetDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Driver daily timesheet export failed:', error);
      let message = 'Failed to export driver daily timesheet.';
      try {
        const data = error?.response?.data;
        if (data instanceof Blob) {
          const text = await data.text();
          try {
            const parsed = JSON.parse(text);
            message = parsed.message || text;
          } catch {
            message = text || message;
          }
        } else if (error?.message) {
          message = error.message;
        }
      } catch {
        /* keep default */
      }
      window.alert(message);
    } finally {
      setAdminExportLoading(false);
      setDriverTimesheetDate('');
    }
  };

  const exportDriverWeeklyTimesheet = async () => {
    if (!driverWeeklyTimesheetMonday) return;
    trackActionLog({
      actionVerb: "Export",
      summary: "Tap export button (driver weekly timesheet)",
      entityType: "export",
      entityId: "driver-weekly-timesheet",
    }).catch(() => {});
    const mmddyyyy = moment(driverWeeklyTimesheetMonday, 'YYYY-MM-DD').format('MM/DD/YYYY');
    setShowDriverWeeklyTimesheetModal(false);
    setAdminExportLoading(true);
    try {
      const response = await ReportService.exportDriverWeeklyTimesheetExcel(mmddyyyy);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute(
        'download',
        `Driver_Weekly_Timesheet_${driverWeeklyTimesheetMonday}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Driver weekly timesheet export failed:', error);
      let message = 'Failed to export driver weekly timesheet.';
      try {
        const data = error?.response?.data;
        if (data instanceof Blob) {
          const text = await data.text();
          try {
            const parsed = JSON.parse(text);
            message = parsed.message || text;
          } catch {
            message = text || message;
          }
        } else if (error?.message) {
          message = error.message;
        }
      } catch {
        /* keep default */
      }
      window.alert(message);
    } finally {
      setAdminExportLoading(false);
      setDriverWeeklyTimesheetMonday('');
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <div className="list row mb-4">
        <Breadcrumb>
          <Breadcrumb.Item href="/">Dashboard</Breadcrumb.Item>
          <Breadcrumb.Item active>
            Admin View
          </Breadcrumb.Item>
        </Breadcrumb>
        <div className="col-md-12 text-primary">
          <h4 className="mb-0">Admin View</h4>
        </div>
      </div>

      <div className="admin-view-tabs-wrap position-relative">
        {adminExportLoading && (
          <div className="admin-view-export-progress px-2 pt-2">
            <ProgressBar striped animated now={100} variant="primary" />
          </div>
        )}
        <Tabs
          activeKey={activeAdminTab}
          onSelect={(k) => k && setActiveAdminTab(k)}
          className="mb-3 admin-view-main-tabs"
        >
        <Tab eventKey="statistics" title="Statistics">
      {/* First Section - No border */}
      <div className="mb-4 admin-view-container">
        <Row>
          {/* Main Content - Full Width */}
          <Col md={12}>
            {/* First Row - Stats Card */}
            <Row className="mb-4">
              <Col md={12}>
                <Card className="admin-view-card">
                  <Card.Body>
                    <Row>
                      {/* Total Members */}
                      <Col md={3} className="text-center">
                        <div className="admin-view-stats-section">
                          <small className="mb-1 d-block admin-view-stats-label">Total Members</small>
                          <h3 className="text-primary">{statsData.totalMembers}</h3>
                          <small
                            className={
                              statsData.totalMembersTrend === 'increase'
                                ? 'text-success'
                                : statsData.totalMembersTrend === 'decrease'
                                  ? 'text-danger'
                                  : 'text-muted'
                            }
                          >
                            {statsData.totalMembersTrend ? (
                              <>
                                {statsData.totalMembersTrend === 'increase' ? '↗' : '↘'}{' '}
                                {statsData.totalMembersIncreaseRate.toFixed(1)}%{' '}
                                {statsData.totalMembersTrend} from last month
                              </>
                            ) : (
                              'No change from last month'
                            )}
                          </small>
                        </div>
                      </Col>
                      {/* Today's Attendance */}
                      <Col md={3} className="text-center">
                        <div className="admin-view-stats-section">
                          <small className="mb-1 d-block admin-view-stats-label">Today's Attendance</small>
                          <h3 className="text-success">{statsData.todayAttendance}</h3>
                          <small className={statsData.todayAttendanceTrend === 'increase' ? 'text-success' : 'text-danger'}>
                            {statsData.todayAttendanceTrend === 'increase' ? '↗' : '↘'} {statsData.todayAttendanceChangeRate.toFixed(1)}% {statsData.todayAttendanceTrend} from yesterday
                          </small>
                        </div>
                      </Col>
                      {/* New Members This Month */}
                      <Col md={3} className="text-center">
                        <div className="admin-view-stats-section">
                          <small className="mb-1 d-block admin-view-stats-label">New Members This Month</small>
                          <h3 className="text-info">{statsData.newMembersThisMonth}</h3>
                          {statsData.newMembersTrend ? (
                            <small className={statsData.newMembersTrend === 'increase' ? 'text-success' : 'text-danger'}>
                              {statsData.newMembersTrend === 'increase' ? '↗' : '↘'}{' '}
                              {statsData.newMembersChangeRate.toFixed(1)}% {statsData.newMembersTrend} from last month
                            </small>
                          ) : null}
                        </div>
                      </Col>
                      {/* Member Discharges This Month */}
                      <Col md={3} className="text-center">
                        <div>
                          <small className="mb-1 d-block admin-view-stats-label">Member Discharges This Month</small>
                          <h3 className="text-warning">{statsData.memberDischargesThisMonth}</h3>
                          <small className={statsData.dischargesTrend === 'increase' ? 'text-danger' : 'text-success'}>
                            {statsData.dischargesTrend === 'increase' ? '↗' : '↘'} {statsData.dischargesChangeRate.toFixed(1)}% {statsData.dischargesTrend} from last month
                          </small>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Second Row - Charts */}
            <Row>
              <Col md={6}>
                <Card className="admin-view-card">
                  <Card.Body>
                    <div className="admin-view-chart-header">
                      <h6 className="admin-view-chart-title">Total Members</h6>
                      <select 
                        value={selectedTrendMonth} 
                        onChange={handleTrendMonthChange}
                        className="form-select form-select-sm admin-view-month-selector"
                      >
                        {generateMonthOptions().map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="admin-view-chart-container">
                      <Line 
                        data={memberTrendData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              display: false
                            },
                            tooltip: {
                              enabled: true
                            }
                          },
                          scales: {
                            y: {
                              ticks: {
                                precision: 0
                              }
                            }
                          },
                          elements: {
                            point: {
                              backgroundColor: 'rgb(75, 192, 192)',
                              borderColor: 'white',
                              borderWidth: 2
                            }
                          }
                        }}
                      />
                      <div className="admin-view-average-display">
                        Average: {memberTrendData.average || 0}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="admin-view-card">
                  <Card.Body>
                    <div className="admin-view-chart-header">
                      <h6 className="admin-view-chart-title">Total Attendance</h6>
                      <select 
                        value={selectedTrendMonth} 
                        onChange={handleTrendMonthChange}
                        className="form-select form-select-sm admin-view-month-selector"
                      >
                        {generateMonthOptions().map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="admin-view-chart-container">
                      {attendanceTrendLoading && !(attendanceTrendData.labels || []).length ? (
                        <div className="admin-view-chart-loading">
                          <Spinner animation="border" size="sm" role="status" aria-label="Loading" />
                          <span>Loading attendance…</span>
                        </div>
                      ) : (
                        <>
                          {attendanceTrendLoading ? (
                            <div className="admin-view-chart-loading admin-view-chart-loading-overlay">
                              <Spinner animation="border" size="sm" role="status" aria-label="Loading" />
                              <span>Loading attendance…</span>
                            </div>
                          ) : null}
                          <Line 
                            data={attendanceTrendData}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: {
                                legend: {
                                  display: false
                                },
                                tooltip: {
                                  enabled: true
                                }
                              },
                              scales: {
                                y: {
                                  ticks: {
                                    precision: 0
                                  }
                                }
                              },
                              elements: {
                                point: {
                                  backgroundColor: 'rgb(255, 99, 132)',
                                  borderColor: 'white',
                                  borderWidth: 2
                                }
                              }
                            }}
                          />
                          <div className="admin-view-average-display">
                            Average: {attendanceTrendData.average || 0}
                          </div>
                        </>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>

        </Row>
      </div>

      {/* Second Section — pie charts: two per row (Pay / New admissions, then Discharge / Admit From) */}
      <div>
        <Row className="g-3 mb-3">
          <Col md={6}>
            <Card className="admin-view-card">
              <Card.Body>
                <h6 className="mb-3">Pay Source</h6>
                <div className="admin-view-chart-container">
                  <Doughnut 
                    data={paySourceData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      layout: {
                        padding: {
                          top: 40,
                          bottom: 40,
                          left: 40,
                          right: 40
                        }
                      },
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: {
                            usePointStyle: true,
                            padding: 20,
                            font: {
                              size: 10
                            },
                            generateLabels: (chart) => {
                              const data = chart.data;
                              if (data.labels.length && data.datasets.length) {
                                return data.labels.map((label, i) => {
                                  const dataset = data.datasets[0];
                                  const value = dataset.data[i];
                                  return {
                                    text: `${label}: ${value}`,
                                    fillStyle: dataset.backgroundColor[i],
                                    strokeStyle: dataset.borderColor[i],
                                    lineWidth: 0,
                                    pointStyle: 'circle',
                                    hidden: false,
                                    index: i
                                  };
                                });
                              }
                              return [];
                            }
                          },
                          align: 'center',
                          maxHeight: 80
                        },
                        tooltip: doughnutHoverTooltip
                      }
                    }}
                    plugins={[{
                      afterDraw: (chart) => {
                        const ctx = chart.ctx;
                        console.log('AfterDraw plugin called', chart.data);
                        console.log('Chart datasets:', chart.data.datasets);
                        console.log('First dataset:', chart.data.datasets[0]);
                        console.log('First dataset data:', chart.data.datasets[0]?.data);
                        
                        const centerX = chart.chartArea.left + (chart.chartArea.right - chart.chartArea.left) / 2;
                        const centerY = chart.chartArea.top + (chart.chartArea.bottom - chart.chartArea.top) / 2;
                        const radius = Math.min(chart.chartArea.right - chart.chartArea.left, chart.chartArea.bottom - chart.chartArea.top) / 2;
                        
                        console.log('Chart center:', centerX, centerY, 'radius:', radius);
                        
                        // Check if data exists
                        if (chart.data.datasets[0] && chart.data.datasets[0].data) {
                          const data = chart.data.datasets[0].data;
                          const labels = chart.data.labels;
                          const total = data.reduce((sum, val) => sum + val, 0);
                          const percentages = data.map(value => total > 0 ? Math.round((value / total) * 100) : 0);
                          
                          console.log('Data found:', data, 'Percentages:', percentages);
                          
                          // Store annotation positions to check for overlaps
                          const annotationPositions = [];
                          
                          data.forEach((value, index) => {
                            const percentage = percentages[index];
                            const label = labels[index];
                            console.log(`Segment ${index}:`, value, percentage, label);
                            
                            if (value > 0) {
                              const angle = chart.getDatasetMeta(0).data[index].startAngle + 
                                          (chart.getDatasetMeta(0).data[index].endAngle - chart.getDatasetMeta(0).data[index].startAngle) / 2;
                              
                              // Calculate points for the line - make it shorter
                              const innerX = centerX + Math.cos(angle) * (radius * 0.95);
                              const innerY = centerY + Math.sin(angle) * (radius * 0.95);
                              const outerX = centerX + Math.cos(angle) * (radius * 1.05);
                              const outerY = centerY + Math.sin(angle) * (radius * 1.05);
                              
                              console.log(`Drawing line for segment ${index}:`, innerX, innerY, outerX, outerY);
                              
                              // Draw the line - make it thinner and lighter
                              ctx.save();
                              ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
                              ctx.lineWidth = 0.5;
                              ctx.beginPath();
                              ctx.moveTo(innerX, innerY);
                              ctx.lineTo(outerX, outerY);
                              ctx.stroke();
                              
                              // Draw the text with better positioning and include label
                              ctx.textAlign = angle > Math.PI/2 && angle < 3*Math.PI/2 ? 'right' : 'left';
                              ctx.textBaseline = 'middle';
                              ctx.font = 'bold 10px Arial';
                              ctx.fillStyle = '#333';
                              
                              // Calculate text position with better bounds checking
                              let textX = outerX + (angle > Math.PI/2 && angle < 3*Math.PI/2 ? -6 : 6);
                              let textY = outerY;
                              
                              // Ensure text stays within chart bounds
                              const textWidth = ctx.measureText(`${label}: ${value} (${percentage}%)`).width;
                              const padding = 15;
                              
                              // Adjust horizontal position if text would go outside chart
                              if (angle > Math.PI/2 && angle < 3*Math.PI/2) {
                                // Left side - check if text would go outside left edge
                                if (textX - textWidth < chart.chartArea.left + padding) {
                                  textX = chart.chartArea.left + padding + textWidth;
                                }
                              } else {
                                // Right side - check if text would go outside right edge
                                if (textX + textWidth > chart.chartArea.right - padding) {
                                  textX = chart.chartArea.right - padding - textWidth;
                                }
                              }
                              
                              // Adjust vertical position if text would go outside top/bottom
                              if (textY < chart.chartArea.top + padding) {
                                textY = chart.chartArea.top + padding;
                              } else if (textY > chart.chartArea.bottom - padding) {
                                textY = chart.chartArea.bottom - padding;
                              }
                              
                              // Check for overlaps with existing annotations
                              const textHeight = 12; // Approximate text height
                              const minDistance = 3; // Minimum distance between annotations
                              
                              let adjusted = false;
                              let attempts = 0;
                              const maxAttempts = 10;
                              
                              while (attempts < maxAttempts) {
                                let hasOverlap = false;
                                
                                for (const existingPos of annotationPositions) {
                                  const distanceX = Math.abs(textX - existingPos.x);
                                  const distanceY = Math.abs(textY - existingPos.y);
                                  
                                  // Check if annotations overlap
                                  if (distanceX < (textWidth + existingPos.width) / 2 + minDistance &&
                                      distanceY < (textHeight + existingPos.height) / 2 + minDistance) {
                                    hasOverlap = true;
                                    break;
                                  }
                                }
                                
                                if (!hasOverlap) {
                                  break;
                                }
                                
                                // Move the annotation to avoid overlap
                                const offset = (attempts + 1) * 10;
                                if (angle > Math.PI/2 && angle < 3*Math.PI/2) {
                                  // Left side - move further left
                                  textX -= offset;
                                } else {
                                  // Right side - move further right
                                  textX += offset;
                                }
                                
                                // Also adjust vertically if needed
                                if (angle > 0 && angle < Math.PI) {
                                  // Top half - move up
                                  textY -= offset * 0.5;
                                } else {
                                  // Bottom half - move down
                                  textY += offset * 0.5;
                                }
                                
                                attempts++;
                              }
                              
                              // Store this annotation position for future overlap checks
                              annotationPositions.push({
                                x: textX,
                                y: textY,
                                width: textWidth,
                                height: textHeight
                              });
                              
                              // Draw label with number and percentage
                              ctx.fillText(`${label}: ${value} (${percentage}%)`, textX, textY);
                              ctx.restore();
                            }
                          });
                        } else {
                          console.log('No data found - datasets[0]:', chart.data.datasets[0]);
                          console.log('No data found - datasets[0].data:', chart.data.datasets[0]?.data);
                        }
                      }
                    }]}
                  />
                  <div className="admin-view-donut-stats">
                    <div className="text-center">
                      <div className="h4 text-primary">{paySourceData.totalActive || 0}</div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="admin-view-card">
              <Card.Body>
                <div className="admin-view-chart-header">
                  <h6 className="admin-view-chart-title">New Admissions</h6>
                  <select 
                    value={selectedMonth} 
                    onChange={handleMonthChange}
                    className="form-select form-select-sm admin-view-month-selector"
                  >
                    {generateMonthOptions().map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="admin-view-chart-container-flexible">
                  <Doughnut 
                    data={newAdmissionsData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      layout: {
                        padding: {
                          top: 30,
                          bottom: 30,
                          left: 30,
                          right: 30
                        }
                      },
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: {
                            usePointStyle: true,
                            padding: 20,
                            font: {
                              size: 10
                            },
                            generateLabels: (chart) => {
                              const data = chart.data;
                              if (data.labels.length && data.datasets.length) {
                                return data.labels.map((label, i) => {
                                  const dataset = data.datasets[0];
                                  const value = dataset.data[i];
                                  return {
                                    text: `${label}: ${value}`,
                                    fillStyle: dataset.backgroundColor[i],
                                    strokeStyle: dataset.borderColor[i],
                                    lineWidth: 0,
                                    pointStyle: 'circle',
                                    hidden: false,
                                    index: i
                                  };
                                });
                              }
                              return [];
                            }
                          },
                          align: 'center'
                        },
                        tooltip: doughnutHoverTooltip
                      }
                    }}
                    plugins={[{
                      afterDraw: (chart) => {
                        const ctx = chart.ctx;
                        console.log('AfterDraw plugin called for New Admissions', chart.data);
                        console.log('Chart datasets:', chart.data.datasets);
                        console.log('First dataset:', chart.data.datasets[0]);
                        console.log('First dataset data:', chart.data.datasets[0]?.data);
                        
                        const centerX = chart.chartArea.left + (chart.chartArea.right - chart.chartArea.left) / 2;
                        const centerY = chart.chartArea.top + (chart.chartArea.bottom - chart.chartArea.top) / 2;
                        const radius = Math.min(chart.chartArea.right - chart.chartArea.left, chart.chartArea.bottom - chart.chartArea.top) / 2;
                        
                        console.log('Chart center:', centerX, centerY, 'radius:', radius);
                        
                        // Check if data exists
                        if (chart.data.datasets[0] && chart.data.datasets[0].data) {
                          const data = chart.data.datasets[0].data;
                          const labels = chart.data.labels;
                          const total = data.reduce((sum, val) => sum + val, 0);
                          const percentages = data.map(value => total > 0 ? Math.round((value / total) * 100) : 0);
                          
                          console.log('Data found:', data, 'Percentages:', percentages);
                          
                          // Store annotation positions to check for overlaps
                          const annotationPositions = [];
                          
                          data.forEach((value, index) => {
                            const percentage = percentages[index];
                            const label = labels[index];
                            console.log(`Segment ${index}:`, value, percentage, label);
                            
                            if (value > 0) {
                              const angle = chart.getDatasetMeta(0).data[index].startAngle + 
                                          (chart.getDatasetMeta(0).data[index].endAngle - chart.getDatasetMeta(0).data[index].startAngle) / 2;
                              
                              // Calculate points for the line - make it shorter
                              const innerX = centerX + Math.cos(angle) * (radius * 0.95);
                              const innerY = centerY + Math.sin(angle) * (radius * 0.95);
                              const outerX = centerX + Math.cos(angle) * (radius * 1.05);
                              const outerY = centerY + Math.sin(angle) * (radius * 1.05);
                              
                              console.log(`Drawing line for segment ${index}:`, innerX, innerY, outerX, outerY);
                              
                              // Draw the line - make it thinner and lighter
                              ctx.save();
                              ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
                              ctx.lineWidth = 0.5;
                              ctx.beginPath();
                              ctx.moveTo(innerX, innerY);
                              ctx.lineTo(outerX, outerY);
                              ctx.stroke();
                              
                              // Draw the text with better positioning and include label
                              ctx.textAlign = angle > Math.PI/2 && angle < 3*Math.PI/2 ? 'right' : 'left';
                              ctx.textBaseline = 'middle';
                              ctx.font = 'bold 10px Arial';
                              ctx.fillStyle = '#333';
                              
                              // Calculate text position with better bounds checking
                              let textX = outerX + (angle > Math.PI/2 && angle < 3*Math.PI/2 ? -6 : 6);
                              let textY = outerY;
                              
                              // Ensure text stays within chart bounds
                              const textWidth = ctx.measureText(`${label}: ${value} (${percentage}%)`).width;
                              const padding = 15;
                              
                              // Adjust horizontal position if text would go outside chart
                              if (angle > Math.PI/2 && angle < 3*Math.PI/2) {
                                // Left side - check if text would go outside left edge
                                if (textX - textWidth < chart.chartArea.left + padding) {
                                  textX = chart.chartArea.left + padding + textWidth;
                                }
                              } else {
                                // Right side - check if text would go outside right edge
                                if (textX + textWidth > chart.chartArea.right - padding) {
                                  textX = chart.chartArea.right - padding - textWidth;
                                }
                              }
                              
                              // Adjust vertical position if text would go outside top/bottom
                              if (textY < chart.chartArea.top + padding) {
                                textY = chart.chartArea.top + padding;
                              } else if (textY > chart.chartArea.bottom - padding) {
                                textY = chart.chartArea.bottom - padding;
                              }
                              
                              // Check for overlaps with existing annotations
                              const textHeight = 12; // Approximate text height
                              const minDistance = 3; // Minimum distance between annotations
                              
                              let adjusted = false;
                              let attempts = 0;
                              const maxAttempts = 10;
                              
                              while (attempts < maxAttempts) {
                                let hasOverlap = false;
                                
                                for (const existingPos of annotationPositions) {
                                  const distanceX = Math.abs(textX - existingPos.x);
                                  const distanceY = Math.abs(textY - existingPos.y);
                                  
                                  // Check if annotations overlap
                                  if (distanceX < (textWidth + existingPos.width) / 2 + minDistance &&
                                      distanceY < (textHeight + existingPos.height) / 2 + minDistance) {
                                    hasOverlap = true;
                                    break;
                                  }
                                }
                                
                                if (!hasOverlap) {
                                  break;
                                }
                                
                                // Move the annotation to avoid overlap
                                const offset = (attempts + 1) * 10;
                                if (angle > Math.PI/2 && angle < 3*Math.PI/2) {
                                  // Left side - move further left
                                  textX -= offset;
                                } else {
                                  // Right side - move further right
                                  textX += offset;
                                }
                                
                                // Also adjust vertically if needed
                                if (angle > 0 && angle < Math.PI) {
                                  // Top half - move up
                                  textY -= offset * 0.5;
                                } else {
                                  // Bottom half - move down
                                  textY += offset * 0.5;
                                }
                                
                                attempts++;
                              }
                              
                              // Store this annotation position for future overlap checks
                              annotationPositions.push({
                                x: textX,
                                y: textY,
                                width: textWidth,
                                height: textHeight
                              });
                              
                              // Draw label with number and percentage
                              ctx.fillText(`${label}: ${value} (${percentage}%)`, textX, textY);
                              ctx.restore();
                            }
                          });
                        } else {
                          console.log('No data found - datasets[0]:', chart.data.datasets[0]);
                          console.log('No data found - datasets[0].data:', chart.data.datasets[0]?.data);
                        }
                      }
                    }]}
                  />
                  <div className="admin-view-donut-stats">
                    <div className="text-center">
                      <div className="h4 text-primary">{newAdmissionsData.totalInMonth || 0}</div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row className="g-3">
          <Col md={6}>
            <Card className="admin-view-card">
              <Card.Body>
                <div className="admin-view-chart-header">
                  <h6 className="admin-view-chart-title">Discharge Reasons</h6>
                  <select 
                    value={selectedDischargeMonth} 
                    onChange={handleDischargeMonthChange}
                    className="form-select form-select-sm admin-view-month-selector"
                  >
                    {generateMonthOptions().map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="admin-view-chart-container-flexible">
                  <Doughnut 
                    data={dischargeReasonsData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      layout: {
                        padding: {
                          top: 30,
                          bottom: 30,
                          left: 30,
                          right: 30
                        }
                      },
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: {
                            usePointStyle: true,
                            padding: 20,
                            font: {
                              size: 10
                            },
                            generateLabels: (chart) => {
                              const data = chart.data;
                              if (data.labels.length && data.datasets.length) {
                                return data.labels.map((label, i) => {
                                  const dataset = data.datasets[0];
                                  const value = dataset.data[i];
                                  return {
                                    text: `${label}: ${value}`,
                                    fillStyle: dataset.backgroundColor[i],
                                    strokeStyle: dataset.borderColor[i],
                                    lineWidth: 0,
                                    pointStyle: 'circle',
                                    hidden: false,
                                    index: i
                                  };
                                });
                              }
                              return [];
                            }
                          },
                          align: 'center'
                        },
                        tooltip: doughnutHoverTooltip
                      }
                    }}
                  />
                  <div className="admin-view-donut-stats">
                    <div className="text-center">
                      <div className="h4 text-primary">
                        {dischargeReasonsData.totalDischarged > 0
                          ? dischargeReasonsData.totalDischarged
                          : 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="admin-view-card">
              <Card.Body>
                <div className="admin-view-chart-header">
                  <h6 className="admin-view-chart-title">Admit From</h6>
                  <select
                    value={selectedMonth}
                    onChange={handleMonthChange}
                    className="form-select form-select-sm admin-view-month-selector"
                  >
                    {generateMonthOptions().map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="text-muted small mb-2">New members in this month (members only; visitors excluded)</div>
                <div className="admin-view-chart-container-flexible">
                  <Doughnut
                    data={admitFromData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      layout: {
                        padding: { top: 20, bottom: 20, left: 20, right: 20 },
                      },
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: {
                            usePointStyle: true,
                            padding: 12,
                            font: { size: 10 },
                          },
                        },
                        tooltip: doughnutHoverTooltip
                      },
                    }}
                  />
                  <div className="admin-view-donut-stats">
                    <div className="text-center">
                      <div className="h4 text-primary">{admitFromData.totalInMonth ?? 0}</div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
        </Tab>
        <Tab eventKey="actionLog" title="Action Log">
          <div className="mb-4 admin-view-container">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() =>
                  setActionLogDate(moment(actionLogDate).subtract(1, 'day').format('YYYY-MM-DD'))
                }
              >
                Previous day
              </button>
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ maxWidth: '11rem' }}
                value={actionLogDate}
                onChange={(e) => setActionLogDate(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() =>
                  setActionLogDate(moment(actionLogDate).add(1, 'day').format('YYYY-MM-DD'))
                }
              >
                Next day
              </button>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => setActionLogDate(moment().format('YYYY-MM-DD'))}
              >
                Today
              </button>
            </div>
            {actionLogLoading ? (
              <div className="text-muted">Loading…</div>
            ) : (
              <Table striped bordered hover responsive size="sm" className="admin-view-action-log-table">
                <thead>
                  <tr>
                    <th style={{ width: '9rem' }}>Time</th>
                    <th style={{ width: '12rem' }}>User</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredActionLogEntries.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-muted">
                        No actions recorded for this date.
                      </td>
                    </tr>
                  ) : (
                    filteredActionLogEntries.map((row, idx) => (
                      <tr key={row._id || row.id || idx}>
                        <td>{moment(row.eventTime).format('LTS')}</td>
                        <td>{row.userDisplayName || row.userId || '—'}</td>
                        <td>{row.summary}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </div>
        </Tab>
        {canViewApiLog && (
        <Tab eventKey="apiLog" title="API log">
          <div className="mb-4 admin-view-container">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() =>
                  setApiLogDate(moment(apiLogDate).subtract(1, 'day').format('YYYY-MM-DD'))
                }
              >
                Previous day
              </button>
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ maxWidth: '11rem' }}
                value={apiLogDate}
                onChange={(e) => setApiLogDate(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() =>
                  setApiLogDate(moment(apiLogDate).add(1, 'day').format('YYYY-MM-DD'))
                }
              >
                Next day
              </button>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => setApiLogDate(moment().format('YYYY-MM-DD'))}
              >
                Today
              </button>
            </div>
            {apiLogLoading ? (
              <div className="text-muted">Loading…</div>
            ) : (
              <Table striped bordered hover responsive size="sm" className="admin-view-action-log-table">
                <thead>
                  <tr>
                    <th style={{ width: '9rem' }}>Time</th>
                    <th style={{ width: '5rem' }}>Method</th>
                    <th>Path</th>
                    <th style={{ width: '6rem' }}>Error</th>
                    <th style={{ width: '10rem' }}>User</th>
                  </tr>
                </thead>
                <tbody>
                  {apiLogEntries.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-muted">
                        No API errors recorded for this date.
                      </td>
                    </tr>
                  ) : (
                    apiLogEntries.map((row, idx) => {
                      const rowId = row._id || row.id || idx;
                      const isExpanded = expandedApiLogId === rowId;
                      const responseMessage =
                        row?.response?.message ||
                        row?.response?.error ||
                        '';
                      return (
                        <React.Fragment key={rowId}>
                          <tr
                            style={{ cursor: 'pointer' }}
                            onClick={() => setExpandedApiLogId(isExpanded ? null : rowId)}
                          >
                            <td>{moment(row.create_date).format('LTS')}</td>
                            <td>{row.method || '—'}</td>
                            <td>{row.originalUrl || row.path || '—'}</td>
                            <td>{row.errorCode || row.statusCode || '—'}</td>
                            <td>{row.username || row.userId || '—'}</td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={5} className="admin-view-api-log-detail">
                                {responseMessage ? (
                                  <div className="mb-2"><strong>Message:</strong> {responseMessage}</div>
                                ) : null}
                                <div className="mb-2">
                                  <strong>Request</strong>
                                  <pre>{JSON.stringify(row.request || {}, null, 2)}</pre>
                                </div>
                                <div>
                                  <strong>Response</strong>
                                  <pre>{JSON.stringify(row.response || {}, null, 2)}</pre>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </Table>
            )}
          </div>
        </Tab>
        )}
        </Tabs>
        <div className="admin-view-export-tab-actions">
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="outline-primary"
              size="sm"
              className="d-inline-flex align-items-center gap-1"
              disabled={adminExportLoading}
            >
              <Download size={16} aria-hidden />
              Export
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => {
                  void runChartDataExport();
                }}
                disabled={adminExportLoading}
              >
                Chart Data
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => openDriverTimesheetModal()}
                disabled={adminExportLoading}
              >
                Driver Daily Timesheet
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => openDriverWeeklyTimesheetModal()}
                disabled={adminExportLoading}
              >
                Driver Weekly Timesheet
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <Modal
        show={showDriverTimesheetModal}
        onHide={() => {
          setShowDriverTimesheetModal(false);
          setDriverTimesheetDate('');
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Driver Daily Timesheet</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Choose a schedule date (today or earlier). Export uses transportation routes for that day.
          </p>
          <div className="field-label mb-1">Date</div>
          <input
            type="date"
            className="form-control"
            max={moment().format('YYYY-MM-DD')}
            value={driverTimesheetDate}
            onChange={(e) => setDriverTimesheetDate(e.target.value)}
          />
          {driverTimesheetDate ? (
            <div className="mt-3">
              <Button
                variant="primary"
                size="sm"
                onClick={() => void exportDriverDailyTimesheet()}
                disabled={adminExportLoading}
              >
                Export
              </Button>
            </div>
          ) : null}
        </Modal.Body>
      </Modal>

      <Modal
        show={showDriverWeeklyTimesheetModal}
        onHide={() => {
          setShowDriverWeeklyTimesheetModal(false);
          setDriverWeeklyTimesheetMonday('');
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Driver Weekly Timesheet</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Pick the Monday that starts the week. Only today or earlier Mondays are allowed (if today
            is Monday, you may select today). Hours match the daily timesheet logic per day; days
            without routes show 0.
          </p>
          <div className="field-label mb-1">Week (Monday)</div>
          <input
            type="date"
            className="form-control"
            max={moment().format('YYYY-MM-DD')}
            value={driverWeeklyTimesheetMonday}
            onChange={onDriverWeeklyTimesheetDateChange}
          />
          {driverWeeklyTimesheetMonday ? (
            <div className="mt-3">
              <Button
                variant="primary"
                size="sm"
                onClick={() => void exportDriverWeeklyTimesheet()}
                disabled={adminExportLoading}
              >
                Export
              </Button>
            </div>
          ) : null}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AdminView; 